import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ShoppingBag, X } from "lucide-react";
import { subscriptionsService } from "../../lib/api/subscriptions";
import { useAuthStore } from "../../stores/authStore";

/**
 * Subtle, dismissable billing notice shown when a Shopify-billed account is in
 * grace_period or read_only. Rendered as a fixed, floating card (bottom-right)
 * so it OVERLAYS rather than reflows the page — it must not push, resize, or
 * otherwise alter the existing layout (an earlier in-flow bar pushed content
 * off-screen). Suspended state is handled by a full-page interstitial.
 *
 * Single CTA: "Reinstall on Shopify". We deliberately do NOT offer an
 * off-platform (Stripe) recovery path for accounts whose billingSource is
 * "shopify" — Shopify App Store reviewers reject any pathway from the
 * embedded-app lifecycle to external billing. See BILLING_FLOWS.html.
 *
 * Dismissal is session-scoped (sessionStorage) and keyed by billingStatus, so
 * an escalation (grace_period → read_only) re-surfaces the notice even after
 * the merchant dismissed the milder one. Polls every 60s so a successful
 * recovery hides it promptly.
 */
function formatDaysLeft(deadline: string | null): number | null {
    if (!deadline) return null;
    const ms = new Date(deadline).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function BillingBanner() {
    const { t } = useTranslation("common");
    const { isAuthenticated } = useAuthStore();
    const [reinstallOpen, setReinstallOpen] = useState(false);

    // Shares the cache entry with SuspendedInterstitial, PlanPage and
    // UpgradeModal — same queryKey AND same queryFn so the cached shape stays
    // consistent across all readers.
    const { data, refetch } = useQuery({
        queryKey: ["subscriptions", "billing-status"],
        queryFn: subscriptionsService.getBillingStatus,
        enabled: isAuthenticated,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
    });
    const billing = data?.data ?? null;
    const status = billing?.billingStatus ?? null;

    // React immediately to webhook-driven changes without waiting for the poll.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = () => refetch();
        window.addEventListener("billing:status_changed", handler);
        return () => window.removeEventListener("billing:status_changed", handler);
    }, [refetch]);

    // Session-scoped dismissal, keyed by status so a more severe state
    // (read_only) re-appears even if grace_period was dismissed this session.
    const dismissKey = status ? `dashbal_billing_banner_dismissed_${status}` : null;
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        if (!dismissKey) {
            setDismissed(false);
            return;
        }
        try {
            setDismissed(window.sessionStorage.getItem(dismissKey) === "1");
        } catch {
            setDismissed(false);
        }
    }, [dismissKey]);

    const handleDismiss = useCallback(() => {
        setDismissed(true);
        if (dismissKey) {
            try {
                window.sessionStorage.setItem(dismissKey, "1");
            } catch {
                /* sessionStorage may be blocked; in-memory state still hides it this session */
            }
        }
    }, [dismissKey]);

    const handleReinstallOnShopify = useCallback(() => {
        // Open the configured Shopify install link (same source AddChannelModal
        // uses). After an uninstall the app is gone from the merchant's
        // /admin/apps list, so deep-linking there is a dead end — they need the
        // install/OAuth entry point. SHOPIFY_APP_LINK is environment-specific
        // (custom-distribution install link on stage, App Store listing on
        // release); fall back to the public listing only if it isn't configured.
        const config = (window as unknown as { __DASHBAL_CONFIG__?: { shopifyAppLink?: string } }).__DASHBAL_CONFIG__;
        const installUrl = config?.shopifyAppLink || "https://apps.shopify.com/dashbal";
        window.open(installUrl, "_blank", "noopener,noreferrer");
        setReinstallOpen(false);
    }, []);

    if (!isAuthenticated || !billing) return null;
    if (billing.billingSource !== "shopify") return null;
    if (status !== "grace_period" && status !== "read_only") return null;
    if (dismissed) return null;

    const isReadOnly = status === "read_only";
    const daysLeft = formatDaysLeft(
        isReadOnly ? billing.readOnlyEndsAt : billing.gracePeriodEndsAt,
    );
    const accentText = isReadOnly ? "text-error-500" : "text-warning-500";

    return (
        <>
            <div className="fixed bottom-4 right-4 z-50 w-[calc(100%-2rem)] max-w-sm animate-fade-in">
                <div className="relative rounded-xl border border-subtle bg-background-elevated shadow-lg p-4">
                    <button
                        type="button"
                        onClick={handleDismiss}
                        aria-label={String(t("billing.banner.dismiss", "Dismiss"))}
                        className="absolute top-2 right-2 p-1 rounded-md text-neutral-400 hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <div className="flex items-start gap-3 pr-5">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accentText}`} />
                        <div className="space-y-1.5">
                            <p className="text-small font-semibold text-text-primary">
                                {isReadOnly
                                    ? t("billing.banner.readOnlyTitle", "Your account is read-only")
                                    : t("billing.banner.graceTitle", "Action needed: restore your Shopify billing")}
                            </p>
                            <p className="text-tiny text-neutral-600 dark:text-neutral-400">
                                {isReadOnly
                                    ? t("billing.banner.readOnlyBody", {
                                          defaultValue: "Restore billing to regain full access. Account fully suspended in {{count}} day.",
                                          count: daysLeft ?? 0,
                                      })
                                    : t("billing.banner.graceBody", {
                                          defaultValue: "Restore billing within {{count}} day or your account will become read-only.",
                                          count: daysLeft ?? 0,
                                      })}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                                {/* "Reinstall on Shopify" only makes sense when the app
                                    is uninstalled. When still installed, the only
                                    recovery shown here is the website path (Shopify
                                    resubscribe is handled inside the embedded app). */}
                                {!billing.appInstalled && (
                                    <button
                                        type="button"
                                        onClick={() => setReinstallOpen(true)}
                                        className="inline-flex items-center gap-1.5 text-tiny font-semibold text-accent-primary hover:underline"
                                    >
                                        <ShoppingBag className="w-3.5 h-3.5" />
                                        {t("billing.banner.reinstallShopify", "Reinstall on Shopify")}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => window.location.assign("/plan")}
                                    className="inline-flex items-center gap-1.5 text-tiny font-semibold text-accent-primary hover:underline"
                                >
                                    {t("billing.banner.subscribeWebsite", "Subscribe on the website")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {reinstallOpen && (
                <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-background-elevated rounded-xl shadow-lg max-w-md w-full p-6 space-y-4 border border-subtle">
                        <h3 className="text-h3 font-display font-semibold text-text-primary">
                            {t("billing.banner.reinstallTitle", "Reinstall Dashbal on Shopify")}
                        </h3>
                        <p className="text-small text-neutral-700 dark:text-neutral-300">
                            {t(
                                "billing.banner.reinstallBody",
                                "You'll be taken to your Shopify admin where you can re-add the Dashbal app to restore billing.",
                            )}
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                className="btn-ghost px-4 py-2 text-small"
                                onClick={() => setReinstallOpen(false)}
                            >
                                {t("common.cancel", "Cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn-primary px-4 py-2 text-small"
                                onClick={handleReinstallOnShopify}
                            >
                                {t("billing.banner.openShopify", "Open Shopify")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default BillingBanner;
